<template>
  <div>
    <v-card
        color="primary"
        class="card"
        :class="{ 'card--ai-running': aiRunning, 'card--ai-done': aiEditDone && !aiRunning }"
        min-width="200"
        @contextmenu="(e) => { onRightClick(e) }"
        @dragover="showDropZone = true"
    >
      <!-- KI-Bearbeitung: läuft / fertig (Nutzer ist meist schon beim nächsten Task) -->
      <div v-if="aiRunning" class="card-ai-badge card-ai-badge--running" :title="aiRunningTitle">
        <v-progress-circular indeterminate size="12" width="2" class="mr-1" />KI-Änderung läuft
      </div>
      <div v-else-if="aiEditDone" class="card-ai-badge card-ai-badge--done" title="Zum Prüfen den Live Editor öffnen">
        <v-icon x-small class="mr-1">mdi-check-circle</v-icon>KI-Änderung fertig
      </div>
      <!-- Platzhalter „wartet auf Kundenänderung" (Änderungslink): alter Task ist geparkt, hier wartet die Änderung -->
      <div v-if="awaitingEdit" class="card-overlay card-overlay--edit d-flex flex-column text-center" :class="{ 'card-overlay--edit-expired': awaitingEditExpired }">
        <v-icon size="64" color="white">{{ awaitingEditExpired ? 'mdi-link-variant-off' : 'mdi-image-edit-outline' }}</v-icon>
        <div class="white--text mt-2 font-weight-bold">{{ awaitingEditExpired ? 'Änderungslink abgelaufen' : 'Wartet auf Kundenänderung' }}</div>
        <div class="white--text text-caption mt-1">
          Link {{ awaitingEdit.mailSentAt ? 'per Mail gesendet' : 'erstellt' }} {{ fmtTs(awaitingEdit.mailSentAt || awaitingEdit.createdAt) }}<br />
          {{ awaitingEditExpired ? 'abgelaufen seit ' + fmtAge(awaitingEdit.expiresAt) : 'gültig bis ' + fmtTs(awaitingEdit.expiresAt) }}
        </div>
        <div class="mt-3 d-flex flex-wrap justify-center" style="gap: 6px">
          <v-btn x-small depressed color="white" :loading="editLinkBusy === 'mail'" :disabled="!!editLinkBusy" @click.stop="editLinkAction('mail')"><v-icon x-small left>mdi-email-fast-outline</v-icon>Mail erneut senden</v-btn>
          <v-btn x-small depressed color="white" :loading="editLinkBusy === 'create'" :disabled="!!editLinkBusy" @click.stop="editLinkAction('create')"><v-icon x-small left>mdi-content-copy</v-icon>Link kopieren</v-btn>
          <v-btn x-small depressed color="warning" :loading="editLinkBusy === 'restore'" :disabled="!!editLinkBusy" @click.stop="editLinkAction('restore')"><v-icon x-small left>mdi-backup-restore</v-icon>Alten Task wiederherstellen</v-btn>
        </div>
      </div>
      <!-- Overlay bei Import / Entfernt -->
      <div v-else-if="imported || task.removed" class="card-overlay">
        <v-icon size="130" color="white">mdi-check-circle</v-icon>
      </div>
      <!-- Overlay: Import läuft auf dem Server (auch für andere Nutzer sichtbar) -->
      <div v-else-if="serverImportRunning" class="card-overlay d-flex flex-column text-center">
        <v-progress-circular indeterminate size="90" width="6" color="white" />
        <div class="white--text mt-3 font-weight-bold">Wird importiert …</div>
      </div>

      <!-- Overlay: Task noch nicht verarbeitet -->
      <div
          v-if="!awaitingEdit && (loadingCustomization || !processed) && !unlockCard"
          class="card-overlay d-flex flex-column text-center"
      >
        <v-icon
            v-if="!loadingCustomization"
            @click="unlockCard = true"
            size="130"
            color="white"
        >
          mdi-lock
        </v-icon>
        <v-progress-circular
            v-else
            :value="true"
            :indeterminate="true"
            size="130px"
        />
        <h2 class="white--text">Waiting For Task Processing</h2>
        <!-- Neue Upscale-Kette: Ablauf des Jobs live -->
        <UpscaleProgress v-if="upscaleJobId" :job-id="upscaleJobId" dark class="mt-3 mx-auto" />
      </div>

      <!-- Dropzone -->
      <div
          v-if="showDropZone"
          @click="showDropZone = false"
          class="d-flex"
      >
        <v-card
            class="card-overlay"
            @dragleave="showDropZone = false"
            @drop.prevent="dropFile"
            @dragenter.prevent
            @dragover.prevent
        >
          <v-icon
              :style="{ 'pointer-events': 'none' }"
              size="130"
              color="white"
          >
            mdi-select-drag
          </v-icon>
          {{ index }}
        </v-card>
      </div>

      <!-- Header -->
      <div class="card-header-function">
        <v-btn
            @click="() => downloadImage(displayImage, `${task.orderName}_${selectedVariantLabel}`, 'jpg')"
            color="primary"
            dark
            small
            fab
            class="card-header-button"
        >
          <v-icon>mdi-download</v-icon>
        </v-btn>

        <TimeIndicator :creation-date="task.createdAt" />

        <v-btn
            @click="openTask"
            color="primary"
            dark
            rounded
            link
            class="card-header-button"
        >
          {{ task.orderName }}
          <v-icon x-small class="ml-3">mdi-open-in-new</v-icon>
        </v-btn>

        <CountryFlagIndicator :country-code="task.shippingCountryCode" />

        <OrderNoteTooltip
            :task="task"
            :on-save="saveOrderNote"
        />

        <div style="cursor: pointer" @click="() => (showPreviousTasks = true)">
          <OrderCountIndicator :order-count="task.orderCount" />
        </div>

        <SupportTooltip :order-id="task.orderId" />
      </div>
      <ExpressLayover :isExpress="task.productType?.isExpress"/>

      <!-- Bildbereich -->
      <v-card-text class="image-container">
        <!-- Hauptbild: wie bei dir weiterhin previewImg -->
        <v-img
            height="500px"
            position="center"
            :src="task.customizationData?.previewImg"
            class="image"
            @click="openImageDialog"
        />
        <div
            :style="{ 'background-color': referrerBackgroundColor }"
            v-if="task.referrer"
            class="task-referrer"
        >
          {{ task.referrer }}
        </div>
        <div class="task-product-extras" v-if="task?.allOrderExtras?.length">
          <div v-for="extra in task.extras" :key="extra">
            + {{extra.name}}
          </div>
<!--          <div v-if="task?.allOrderExtras?.length > task.extras?.length" style="font-size: 8px">-->
<!--            🚨CHECK WITH ORDER 🚨-->
<!--            <div v-for="extra in task?.allOrderExtras" :key="extra">-->
<!--              ! {{extra.name}}-->
<!--            </div>-->
<!--          </div>-->
        </div>
        <div class="task-product-name">
          {{ task.productType.name }}<br />
          {{ task?.customPrintVariantName || task.printVariantName }}
        </div>

        <!-- Original-Vorschau -->
        <v-img
            v-if="originalImage"
            class="original-image"
            width="150px"
            :src="originalImage"
            @click="openImageDialog(UPSCALER_VARIANTS.ORIGINAL)"
        />

        <!-- Weitere Originale (Foto-Kombination: 2–4 Fotos) -->
        <v-img
            v-for="(orig, oi) in additionalOriginals"
            :key="'orig-' + oi"
            class="original-image original-image-2"
            width="150px"
            :src="orig"
            @click="dialogImage = orig"
        />
      </v-card-text>

      <!-- Actions -->
      <v-card-actions class="card-actions">
        <!-- KI-Edits: Anzahl + Hinweis, wenn gerade einer erstellt wird -->
        <v-chip
            v-if="aiEditRunning"
            small
            color="orange darken-1"
            text-color="white"
            class="ml-2"
        >
          <v-progress-circular indeterminate size="10" width="2" class="mr-1" color="white" />
          Edit läuft
        </v-chip>
        <v-chip
            v-else-if="aiEditCount"
            small
            outlined
            color="white"
            text-color="white"
            class="ml-2"
        >
          <v-icon x-small left>mdi-creation</v-icon>{{ aiEditCount }} Edit{{ aiEditCount > 1 ? "s" : "" }}
        </v-chip>
        <!-- Task-Score der neuen Upscale-Kette (niedrigster Wert aus Regionen, Stil, Inhalt) -->
        <v-tooltip v-if="upscaleScore !== null" top>
          <template v-slot:activator="{ on, attrs }">
            <v-chip small class="ml-2" color="white" v-bind="attrs" v-on="on">
              <b :style="{ color: upscaleScoreColor }">Score {{ upscaleScore }}</b>
            </v-chip>
          </template>
          <span>{{ upscaleScoreHint }}</span>
        </v-tooltip>
        <v-tooltip v-if="serverImportError" top max-width="320">
          <template v-slot:activator="{ on, attrs }">
            <v-chip small class="ml-2" color="red darken-1" text-color="white" v-bind="attrs" v-on="on">
              <v-icon x-small left>mdi-alert-circle-outline</v-icon>Lokal importieren
            </v-chip>
          </template>
          <span>Server-Import fehlgeschlagen — bitte lokal importieren (Editor öffnet im Modus „Lokal"). {{ serverImportError }}</span>
        </v-tooltip>
        <v-tooltip v-if="upscaleError" top max-width="320">
          <template v-slot:activator="{ on, attrs }">
            <v-chip small class="ml-2" color="red darken-1" text-color="white" v-bind="attrs" v-on="on">
              <v-icon x-small left>mdi-alert-circle-outline</v-icon>Upscale
            </v-chip>
          </template>
          <span>Upscale-Job fehlgeschlagen — Fallback aktiv. {{ upscaleError }}</span>
        </v-tooltip>
        <!-- Auto-Import: Restzeit + Stop, sonst „Manuell · Grund" (Score, kein Job, Ausschluss …) -->
        <AutoImportCountdown :task="task" />
        <!-- Varianten-Chip nur für Sonder-Varianten (Pro/Clean) — „Default" ist der Normalfall und braucht keinen Chip -->
        <v-chip
            v-if="selectedVariantLabel && selectedVariant !== UPSCALER_VARIANTS.DEFAULT"
            small
            outlined
            class="ml-2"
            color="white"
            text-color="white"
            @click="resetSelectedVariant"
        >
          {{ selectedVariantLabel }}
          <v-icon size="small">mdi-close</v-icon>
        </v-chip>

        <v-btn dark color="success" small @click="openLiveEditor">
          <v-icon left>mdi-pencil</v-icon> Live Editor
        </v-btn>
      </v-card-actions>
    </v-card>

    <!-- Dialog für Bild + Upscaling -->
    <ImageDialog
        v-if="dialogImage"
        :show="!!dialogImage"
        :image="dialogImage"
        :onClose="closeImageDialog"
    >
      <div
          class="switch-upscale-container"
          v-if="dialogImage !== originalImage && !additionalOriginals.includes(dialogImage)"
      >
        <v-btn
            ripple
            class="mr-3"
            :dark="selectedVariant === UPSCALER_VARIANTS.ORIGINAL"
            @click="handleUpscaleClick(UPSCALER_VARIANTS.ORIGINAL)"
        >
          Original
        </v-btn>

        <v-btn
            class="mr-3"
            :loading="upscaleLoading[UPSCALER_VARIANTS.DEFAULT]"
            :dark="selectedVariant === UPSCALER_VARIANTS.DEFAULT"
            @click="handleUpscaleClick(UPSCALER_VARIANTS.DEFAULT)"
        >
          <v-icon>mdi-auto-fix</v-icon>
          Default
        </v-btn>

        <v-btn
            class="mr-3"
            :loading="upscaleLoading[UPSCALER_VARIANTS.PRO_CAR]"
            :dark="selectedVariant === UPSCALER_VARIANTS.PRO_CAR"
            @click="handleUpscaleClick(UPSCALER_VARIANTS.PRO_CAR)"
        >
          <v-icon>mdi-auto-fix</v-icon>
          Pro (Car)
        </v-btn>

        <v-btn
            v-if="upscaleUrls[UPSCALER_VARIANTS.CLEAN_PERSON] !== undefined"
            :loading="upscaleLoading[UPSCALER_VARIANTS.CLEAN_PERSON]"
            :dark="selectedVariant === UPSCALER_VARIANTS.CLEAN_PERSON"
            @click="handleUpscaleClick(UPSCALER_VARIANTS.CLEAN_PERSON)"
        >
          <v-icon>mdi-auto-fix</v-icon>
          Clean Person
        </v-btn>

        <v-btn
            v-if="upscaleUrls[UPSCALER_VARIANTS.PRO_PERSON] !== undefined"
            :loading="upscaleLoading[UPSCALER_VARIANTS.PRO_PERSON]"
            :dark="selectedVariant === UPSCALER_VARIANTS.PRO_PERSON"
            @click="handleUpscaleClick(UPSCALER_VARIANTS.PRO_PERSON)"
        >
          <v-icon>mdi-auto-fix</v-icon>
          Pro (Person)
        </v-btn>
      </div>
    </ImageDialog>

    <!-- Kontextmenü -->
    <ListMenu
        :show="showMenu"
        :x="x"
        :y="y"
        :list-items="menuListItems"
    />

    <!-- Mail-Dialog -->
    <OrderMailDialog
        v-if="showMailDialog"
        :show="showMailDialog"
        :v2="true"
        :wait-for-sent-success="false"
        :on-close="() => (showMailDialog = false)"
        :order-id="task.orderId"
        mail-category="IMPORT"
    />

    <LiveEditorDialog
        ref="liveEditor"
        v-show="showLiveEditor"
        v-model="showLiveEditor"
        :task="task"
        :customizationData="task.customizationData"
        :orderName="task.orderName"
        :upscaledUrls="upscaledUrlsForDialogs"
        @save="handleLiveEditorSave"
        :refinement-artwork="refinementImageUrl"
        @approveProcessed="showLiveEditor = false"
        @taskCompleted="onTaskCompleted"
        @editStatus="onEditStatus"
        :server-import="!reimport"
        @serverImport="pushToServerImport"
        @approve="({ finalUrl, sendPreviewCustomer, refinementUrl, currentConfig }) => pushToFulfill(finalUrl, sendPreviewCustomer, refinementUrl, currentConfig)"
        @refinement="pushToRefinement"
    />

    <!-- Schutz vor Doppel-Import -->
    <v-dialog :value="!!alreadyImportedMessage" max-width="420" @input="alreadyImportedMessage = ''">
      <v-card>
        <v-card-title class="text-h6"><v-icon left color="orange darken-2">mdi-alert-circle-outline</v-icon>Bereits importiert</v-card-title>
        <v-card-text>{{ alreadyImportedMessage }}</v-card-text>
        <v-card-actions><v-spacer /><v-btn text color="primary" @click="alreadyImportedMessage = ''">OK</v-btn></v-card-actions>
      </v-card>
    </v-dialog>

    <PreviousTasksDialog
        v-if="showPreviousTasks"
        :show="!!showPreviousTasks"
        :on-close="() => (showPreviousTasks = false)"
        :task="task"
    />
  </div>
</template>

<script>
import { getStorage, ref, getDownloadURL } from "firebase/storage";
import { mapActions, mapGetters } from "vuex";
import ImageDialog from "@/components/ImageDialog.vue";
import TimeIndicator from "@/components/TaskCards/components/TimeIndicator.vue";
import { downloadImageFromUrl, getTrackingUrl, openOrderModal , getOrderStatusUrl} from "@/utils";
import OrderNoteTooltip from "@/components/OrderNoteTooltip.vue";
import OrderCountIndicator from "@/components/TaskCards/components/OrderCountIndicator.vue";
import CountryFlagIndicator from "@/components/TaskCards/components/CountryFlagIndicator.vue";
import SupportTooltip from "@/components/Support/SupportTooltip";
import ListMenu from "@/components/ListMenu.vue";
import OrderMailDialog from "@/components/OrderMailDialog.vue";
import { getFunctions, httpsCallable } from "firebase/functions";
import { EventBus, events } from "../../../event-bus";
import LiveEditorDialog from "@/components/FulfillOrders/LiveEditorDialog.vue";
import PreviousTasksDialog from "@/components/TaskCards/components/PreviousTasksDialog.vue";
import { permissions } from "@/constants";
import { deleteDoc, doc, getDoc } from "firebase/firestore";
import { app, db } from "../../../fb";
import ExpressLayover from "@/components/TaskCards/ExpressLayover.vue";
import UpscaleProgress from "@/components/Upscales/UpscaleProgress.vue";
import AutoImportCountdown from "@/components/TaskCards/components/AutoImportCountdown.vue";
import { scoreColor } from "@/upscaleJobs";

const UPSCALER_VARIANTS = {
  ORIGINAL: "ORIGINAL",
  DEFAULT: "DEFAULT",
  PRO_CAR: "PRO_CAR",
  PRO_PERSON: "PRO_PERSON",
  CLEAN_PERSON: "CLEAN_PERSON",
};

export default {
  name: "ImportTaskCard",
  components: {
    AutoImportCountdown,
    UpscaleProgress,
    ExpressLayover,
    PreviousTasksDialog,
    LiveEditorDialog,
    OrderMailDialog,
    ListMenu,
    OrderCountIndicator,
    CountryFlagIndicator,
    OrderNoteTooltip,
    TimeIndicator,
    ImageDialog,
    SupportTooltip,
  },
  props: {
    task: { required: true },
    index: { type: Number, required: true },
    resultsInTask: { type: Boolean, default: false },
    /** Review/erneuter Import eines schon importierten Tasks: kein Doppel-Schutz, kein Server-Import (nur bei Live-Tasks im Laden) */
    reimport: { type: Boolean, default: false },
    selectedEditStyle: { default: "" },
  },

  data() {
    return {
      editLinkBusy: null,
      showPreviousTasks: false,
      showLiveEditor: false,
      loadingCustomization: false,
      UPSCALER_VARIANTS,

      imported: false,
      // KI-Bearbeitung (Region-/Feedback-/4K-Edit) am Task: läuft bzw. seit dem letzten Öffnen fertig geworden
      aiEditRunning: false,
      aiEditDone: false,
      alreadyImportedMessage: "",
      unlockCard: false,
      showDropZone: false,

      refinementImage: null,
      refinementImageUrl: null,

      dialogImage: null,

      isUploading: false,
      showMenu: false,
      showMailDialog: false,
      x: 0,
      y: 0,

      // UI state only
      upscaleLoading: {
        [UPSCALER_VARIANTS.DEFAULT]: false,
        [UPSCALER_VARIANTS.PRO_CAR]: false,
        [UPSCALER_VARIANTS.PRO_PERSON]: false,
        [UPSCALER_VARIANTS.CLEAN_PERSON]: false,
      },

      // Auswahl bleibt stateful
      selectedVariant: UPSCALER_VARIANTS.ORIGINAL,

      menuListItems: [],
    };
  },

  computed: {
    /** Platzhalter eines Änderungslinks (orderEdit.js placeholderTaskFor) */
    awaitingEdit() {
      return (this.task && this.task.awaitingCustomerEdit) || null;
    },
    awaitingEditExpired() {
      return !!this.awaitingEdit && Date.now() >= Number(this.awaitingEdit.expiresAt || 0);
    },
    ...mapGetters(["userPermissions", "isAdmin", "currentUser"]),

    processed() {
      return !this.task?.customizationData?.isProcessing;
    },

    customizationData() {
      return this.task?.customizationData || {};
    },
    aiEditCount() {
      return Array.isArray(this.customizationData?.aiEdits)
          ? this.customizationData.aiEdits.length
          : 0;
    },
    /** KI läuft laut Task-Daten: Region-Edits (vom Server gespiegelt), Auto-Edit aus Bewertungen oder 4K-Edit */
    aiRunningFromTask() {
      const cd = this.customizationData || {};
      return !!(cd.regionEditsRunning && cd.regionEditsRunning.count) || !!cd.aiEditPending || this.task?.feedback?.autoEdit?.status === "running";
    },
    /** Rand + Badge: Task-Daten ODER Meldung des eingebetteten Editors */
    aiRunning() {
      return this.aiRunningFromTask || this.aiEditRunning;
    },
    aiRunningTitle() {
      const r = this.customizationData?.regionEditsRunning;
      if (r && r.count) return `${r.count} Bereich${r.count > 1 ? "e" : ""} in Bearbeitung${r.labels && r.labels.length ? ": " + r.labels.join(", ") : ""}`;
      if (this.task?.feedback?.autoEdit?.status === "running") return "Auto-Edit aus Kundenbewertung läuft";
      return "KI-Änderung läuft";
    },

    // Neue Upscale-Kette (upscaleJobs): Job-ID, Kurzfassung und Fehler stehen am Task
    upscaleJobId() {
      return this.customizationData?.upscaleJobId || null;
    },
    upscaleScore() {
      const min = this.customizationData?.upscale?.taskScore?.min;
      return min === null || min === undefined ? null : min;
    },
    upscaleScoreColor() {
      return scoreColor(this.upscaleScore, this.customizationData?.upscale?.taskScoreLimit || 85);
    },
    upscaleScoreHint() {
      const up = this.customizationData?.upscale || {};
      const s = up.taskScore || {};
      const limit = up.taskScoreLimit ? ` · Limit ${up.taskScoreLimit}: ${up.passed ? "erreicht" : "nicht erreicht"}` : "";
      return `Niedrigster Wert: ${s.limitedBy || "–"} · Durchschnitt ${s.avg ?? "–"}${limit}`;
    },
    /** Import läuft gerade auf dem Server (Marke am Task; nach 6 Minuten gilt sie als hängengeblieben) */
    serverImportRunning() {
      const s = this.task && this.task.serverImport;
      return this.isUploading || !!(s && s.state === "running" && Date.now() - (s.startedAt || 0) < 6 * 60 * 1000);
    },
    /** Server-Import gescheitert → Task muss lokal importiert werden */
    serverImportError() {
      const s = this.task && this.task.serverImport;
      return s && s.state === "failed" ? s.error || "Unbekannter Fehler" : "";
    },
    upscaleError() {
      return this.customizationData?.upscaleError || "";
    },

    artworkImage() {
      return this.customizationData?.artworkImageUrl;
    },

    originalImage() {
      return this.customizationData?.originalImage;
    },
    originalImage2() {
      return this.customizationData?.originalImage2;
    },
    // Alle weiteren Quell-Fotos außer dem ersten (Kombination mit 2–4 Fotos);
    // Alt-Tasks kennen nur originalImage2
    additionalOriginals() {
      const cd = this.customizationData || {};
      if (Array.isArray(cd.originalImages) && cd.originalImages.length >= 2) {
        return cd.originalImages.slice(1);
      }
      return cd.originalImage2 ? [cd.originalImage2] : [];
    },

    adminTasksAllowed() {
      return this.isAdmin || this.userPermissions.includes(permissions.MANAGE_TASKS);
    },

    // ✅ Upscale URLs IMMER aus Task ableiten (Single Source of Truth)
    upscaleUrls() {
      const c = this.customizationData || {};

      // WICHTIG: Feldnamen anpassen, falls deine Pro/Clean URLs anders heißen.
      // DEFAULT nutzt bei dir "upscaledImage" – die anderen sind bei dir bisher nur lokal erzeugt.
      return {
        [UPSCALER_VARIANTS.DEFAULT]: c.upscaledImage || null,
        [UPSCALER_VARIANTS.PRO_CAR]: c.upscaledImageProCar ?? null,
        [UPSCALER_VARIANTS.PRO_PERSON]: c.upscaledImageProPerson ?? null,
        [UPSCALER_VARIANTS.CLEAN_PERSON]: c.upscaledImageCleanPerson ?? null,
      };
    },

    // Form wie vorher (für LiveEditorDialog), aber ohne Duplizieren der URLs in data()
    upscaledUrlsForDialogs() {
      return {
        [UPSCALER_VARIANTS.DEFAULT]: {
          loading: this.upscaleLoading[UPSCALER_VARIANTS.DEFAULT],
          url: this.upscaleUrls[UPSCALER_VARIANTS.DEFAULT],
        },
        [UPSCALER_VARIANTS.PRO_CAR]: {
          loading: this.upscaleLoading[UPSCALER_VARIANTS.PRO_CAR],
          url: this.upscaleUrls[UPSCALER_VARIANTS.PRO_CAR],
        },
        [UPSCALER_VARIANTS.PRO_PERSON]: {
          loading: this.upscaleLoading[UPSCALER_VARIANTS.PRO_PERSON],
          url: this.upscaleUrls[UPSCALER_VARIANTS.PRO_PERSON],
        },
        [UPSCALER_VARIANTS.CLEAN_PERSON]: {
          loading: this.upscaleLoading[UPSCALER_VARIANTS.CLEAN_PERSON],
          url: this.upscaleUrls[UPSCALER_VARIANTS.CLEAN_PERSON],
        },
      };
    },

    // Basisbild für Upscaling
    taskImage() {
      return this.refinementImageUrl || this.artworkImage || this.task.imgThumb;
    },

    // Was im Dialog/Download angezeigt werden soll
    displayImage() {
      if (this.refinementImageUrl) return this.refinementImageUrl;

      if (this.selectedVariant === UPSCALER_VARIANTS.ORIGINAL) {
        return this.taskImage;
      }

      return this.upscaleUrls[this.selectedVariant] || this.taskImage;
    },

    selectedVariantLabel() {
      if (this.refinementImage) return "Refinement Image";

      switch (this.selectedVariant) {
        case UPSCALER_VARIANTS.DEFAULT:
          return "Default";
        case UPSCALER_VARIANTS.PRO_CAR:
          return "Pro (Car)";
        case UPSCALER_VARIANTS.PRO_PERSON:
          return "Pro (Person)";
        case UPSCALER_VARIANTS.CLEAN_PERSON:
          return "Clean (Person)";
        case UPSCALER_VARIANTS.ORIGINAL:
        default:
          return null;
      }
    },
  },

  beforeDestroy() {
    EventBus.$off("live-editor-open-task", this._openOnCall);
    EventBus.$off("live-editor-preload", this._preloadOnCall);
  },
  mounted() {
    // Review-Flow: CardsGrid ruft den nächsten Task auf
    this._openOnCall = (uid) => {
      if (uid !== this.task?.uid) return;
      // Schon importiert, entfernt oder Import läuft → überspringen, CardsGrid nimmt den nächsten
      if (this.imported || this.task?.removed || this.serverImportRunning || this.isUploading) {
        EventBus.$emit("live-editor-task-done", this.task.uid);
        return;
      }
      this.openLiveEditor();
    };
    EventBus.$on("live-editor-open-task", this._openOnCall);
    // Vorladen auf Zuruf (nächste Tasks nach dem gerade geöffneten)
    this._preloadOnCall = (uid) => {
      if (uid === this.task?.uid && this.$refs.liveEditor && this.$refs.liveEditor.preloadAssets) this.$refs.liveEditor.preloadAssets();
    };
    EventBus.$on("live-editor-preload", this._preloadOnCall);
    EventBus.$on(events.TASK_UPDATED, ({ uid }) => {
      if (this.task.uid === uid) {
        this.$forceUpdate();  // Nur wenn nötig
        console.log("UPDATE TASK!", uid)
      }
    });
  },

  created() {
    // optional: initial default auswählen, wenn schon vorhanden
    if (this.upscaleUrls[UPSCALER_VARIANTS.DEFAULT]) {
      this.selectedVariant = UPSCALER_VARIANTS.DEFAULT;
    }

    this.menuListItems = [
      {
        icon: "mdi-download",
        onClick: () =>
            this.downloadImage(
                this.artworkImage || this.displayImage,
                `${this.task.orderName}_artwork`,
                "png"
            ),
        label: "Download Artwork",
      },
      {
        icon: "mdi-download",
        onClick: () =>
            this.downloadImage(
                this.originalImage || this.taskImage,
                `${this.task.orderName}_original`,
                "png"
            ),
        label: "Download Original",
      },
      {
        icon: "mdi-download",
        onClick: () =>
            this.downloadImage(
                this.upscaleUrls[UPSCALER_VARIANTS.DEFAULT] || this.displayImage,
                `${this.task.orderName}_default_upscale`,
                "png"
            ),
        label: "Download DEFAULT upscale",
      },
      {
        icon: "mdi-refresh",
        onClick: () => this.getCustomizationData(),
        label: "Get Customization Data",
      },
      {
        icon: "mdi-reload",
        onClick: () => this.refreshCustomization(),
        label: "Refresh Customization Data",
      },
      {
        icon: "mdi-close-circle",
        onClick: () => this.cancelTask({task: this.task}),
        label: "Cancel Task",
      },
      {
        icon: "mdi-email-outline",
        onClick: () => {
          this.showMailDialog = true;
        },
        label: "Send Mail",
        hide: !this.adminTasksAllowed,
      },
      {
        icon: "mdi-email-search-outline",
        onClick: this.searchOrderInMails,
        label: "Search For Response",
        hide: !this.adminTasksAllowed,
      },
      {
        icon: "mdi-clipboard-arrow-right",
        onClick: this.copyOrderTrackingUrl,
        label: "Copy Order Tracking URL",
      },
      {
        icon: "mdi-delete",
        onClick: this.deleteFromImport,
        label: "Delete From Import",
      },
    ];
  },

  watch: {
    // Wenn DEFAULT später neu reinkommt → optional automatisch auswählen
    "task.customizationData.upscaledImage": {
      immediate: true,
      handler(newUrl) {
        if (!newUrl) return;

        // Nur “auto-switch”, wenn der User noch auf ORIGINAL ist
        if (this.selectedVariant === UPSCALER_VARIANTS.ORIGINAL) {
          this.selectedVariant = UPSCALER_VARIANTS.DEFAULT;
        }

        // Wenn Dialog offen und selectedVariant != ORIGINAL -> sofort aktualisieren
        if (this.dialogImage && this.selectedVariant !== UPSCALER_VARIANTS.ORIGINAL) {
          this.dialogImage = this.displayImage;
        }
      },
    },
  },

  methods: {
    fmtTs(ms) {
      return ms ? new Date(Number(ms)).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "";
    },
    fmtAge(ms) {
      const h = Math.max(0, Math.round((Date.now() - Number(ms || 0)) / 3600000));
      return h < 48 ? `${h} Std.` : `${Math.round(h / 24)} Tagen`;
    },
    /** Änderungslink-Aktionen auf dem Platzhalter: mail / create (kopieren) / restore (alten Task zurück) */
    async editLinkAction(action) {
      if (action === "restore" && !window.confirm("Alten Task wiederherstellen? Der Änderungslink wird ungültig, der Platzhalter verschwindet.")) return;
      this.editLinkBusy = action;
      try {
        const fn = httpsCallable(getFunctions(), "orderEditLink", { timeout: 60000 });
        const { data } = await fn({ action, orderUid: String(this.task.orderId || ""), taskUid: String(this.task.uid) });
        if (action === "create") {
          try { await navigator.clipboard.writeText(data.url); } catch (e) { console.warn(e); }
          EventBus.$emit(events.SNACKBAR, { message: "Änderungslink kopiert, gültig bis " + this.fmtTs(data.expiresAt), type: "success" });
        } else if (action === "mail") {
          EventBus.$emit(events.SNACKBAR, { message: `Mail an ${data.mailTo} gesendet, Link gültig bis ${this.fmtTs(data.expiresAt)}`, type: "success" });
        } else {
          EventBus.$emit(events.SNACKBAR, { message: `Task ${data.restoredTaskUid} wiederhergestellt`, type: "success" });
        }
      } catch (e) {
        console.error("Änderungslink:", e);
        EventBus.$emit(events.SNACKBAR, { message: `Änderungslink: ${e.message || e}`, type: "error" });
      } finally {
        this.editLinkBusy = null;
      }
    },
    ...mapActions(["editTask", "cancelTask", "importLiveTaskFromShopify", "importLiveTaskRefinement"]),

    openTask() {
      openOrderModal(this.task.orderId);
    },

    onTaskCompleted(payload) {
      this.showLiveEditor = false;
      // Auto-ReOpen ist abschaltbar (Toggle im Editor, Geräte-Einstellung)
      if (payload && payload.autoNext === false) return;
      // CardsGrid ermittelt den nächsten Task und öffnet dessen Editor
      EventBus.$emit("live-editor-task-done", this.task?.uid);
    },
    /** Vom eingebetteten Editor gemeldet (läuft auch bei geschlossenem Dialog, Job-Abo ist aktiv) */
    onEditStatus({ running }) {
      if (running) {
        this.aiEditRunning = true;
        this.aiEditDone = false;
      } else if (this.aiEditRunning) {
        this.aiEditRunning = false;
        this.aiEditDone = true;
      }
    },
    openLiveEditor() {
      this.showLiveEditor = true;
      this.aiEditDone = false;
      // CardsGrid lädt daraufhin die Bilder der nächsten Tasks vor
      EventBus.$emit("live-editor-opened", this.task?.uid);
    },

    async handleLiveEditorSave(payload) {
      console.log("Saving Changes:", payload);
      EventBus.$emit(events.SNACKBAR, {
        message: "Changes saved locally (implement API call here)",
        type: "success",
      });
    },

    resetSelectedVariant() {
      this.refinementImage = null;
      this.refinementImageUrl = null;
      this.selectedVariant = UPSCALER_VARIANTS.ORIGINAL;

      if (this.dialogImage) this.dialogImage = this.displayImage;
    },

    saveOrderNote({orderNote, orderNoteFiles}) {
      this.editTask({
        task: {...this.task, orderNote, orderNoteFiles},
      });
    },

    async deleteFromImport() {
      const taskToHide = doc(db, "shopifyTasks", this.task.uid);
      await deleteDoc(taskToHide);
    },

    onRightClick(e) {
      e.preventDefault();
      this.showMenu = false;
      this.x = e.clientX;
      this.y = e.clientY;
      this.$nextTick(() => {
        this.showMenu = true;
      });
    },

    async refreshCustomization() {
      this.loadingCustomization = true;
      try {
        const functions = getFunctions();
        const refreshCustomizationForTask = httpsCallable(functions, "refreshCustomizationForTask");
        await refreshCustomizationForTask({taskUid: this.task.uid});
      } catch (error) {
        console.error("Refresh customization failed:", error);
        EventBus.$emit(events.SNACKBAR, {
          message: "Fehler beim Customization data!",
          type: "error",
        });
      } finally {
        this.loadingCustomization = false;
      }
    },

    /** Schon importiert (tasks/{uid} existiert) oder läuft gerade ein Server-Import? Dann nie ein zweites Mal. */
    async alreadyImported() {
      const running = this.task.serverImport && this.task.serverImport.state === "running" && Date.now() - (this.task.serverImport.startedAt || 0) < 6 * 60 * 1000;
      if (running) return "wird gerade auf dem Server importiert";
      const existing = await getDoc(doc(db, "tasks", String(this.task.uid)));
      if (!existing.exists()) return "";
      // liegengebliebenen Live-Task wegräumen
      try {
        await deleteDoc(doc(db, "shopifyTasks", this.task.uid));
      } catch (e) {
        console.log("Task was already removed");
      }
      return "wurde bereits importiert";
    },

    async pushToFulfill(url = null, sendPreviewCustomer = false, refinementUrl, currentConfig) {
      this.showLiveEditor = false;
      // Schutz vor Doppel-Import (früher: Task bei einem Nutzer noch sichtbar → zweimal produziert) — nicht im Review (dort ist er gewollt importiert)
      const blocked = this.reimport ? "" : await this.alreadyImported();
      if (blocked) {
        this.alreadyImportedMessage = `${this.task.orderName} ${blocked} — nicht erneut importiert.`;
        return;
      }
      this.isUploading = true;
      this.importLiveTaskFromShopify({
        task: {...this.task},
        finalArtworkUrl: url,
        refinementUrl,
        currentConfig,
        callBack: () => {
          this.isUploading = false;
          this.imported = true;
        },
        sendPreviewCustomer,
      });
    },

    /**
     * Server-Modus des Editors: Der Server rendert die Druckdatei und importiert den Task
     * (importLiveTaskCall — derselbe Weg wie der Auto-Import). Läuft im Hintergrund auf der Karte,
     * der Editor ist schon beim nächsten Task. Die Vorschau-Mail („Preview") verschickt der Server. Fehler: Der Server markiert den Task
     * (serverImport.state = "failed") → Chip „Lokal importieren".
     */
    async pushToServerImport({ payload, sendPreviewCustomer, refinementUrl }) {
      this.isUploading = true;
      this.showLiveEditor = false;
      try {
        const call = httpsCallable(getFunctions(app, "europe-west1"), "importLiveTaskCall", { timeout: 300000 });
        // sendPreview: „Preview" = Vorschau-Mail an den Kunden (verschickt der Server), „Fulfill" = ohne
        const { data } = await call({ ...payload, refinementUrl: refinementUrl || null, sendPreview: !!sendPreviewCustomer });
        if (data && data.alreadyImported) {
          this.alreadyImportedMessage = `${this.task.orderName} wurde bereits importiert — nicht erneut importiert.`;
        } else if (data && data.mailError) {
          EventBus.$emit(events.SNACKBAR, { message: `${this.task.orderName} importiert, aber die Vorschau-Mail ist fehlgeschlagen: ${data.mailError}`, type: "error" });
        }
        this.imported = true;
      } catch (error) {
        console.error("Server-Import fehlgeschlagen:", error);
        EventBus.$emit(events.SNACKBAR, {
          message: `Server-Import von ${this.task.orderName} fehlgeschlagen — bitte lokal importieren. ${(error && error.message) || ""}`,
          type: "error",
        });
      } finally {
        this.isUploading = false;
      }
    },

    pushToRefinement({revisionComment, customOriginalImage, refinementUrl, currentConfig}) {
      this.importLiveTaskRefinement({
        task: this.task,
        revisionComment,
        customOriginalImage,
        refinementUrl,
        currentConfig,
        callBack: () => {
          this.isUploading = false;
          this.imported = true;
        },
      });
    },

    dropFile(e) {
      e.preventDefault();
      this.showDropZone = false;

      const droppedFiles = e.dataTransfer ? e.dataTransfer.files : e.target.files;
      if (!droppedFiles || !droppedFiles.length) return;

      this.refinementImage = droppedFiles[0];

      const reader = new FileReader();
      reader.onload = (event) => {
        this.refinementImageUrl = event.target.result;
        if (this.dialogImage) this.dialogImage = this.displayImage;
      };
      reader.readAsDataURL(droppedFiles[0]);
    },

    openImageDialog(variant) {
      if (variant === UPSCALER_VARIANTS.ORIGINAL) {
        this.dialogImage = this.originalImage;
      } else {
        this.dialogImage = this.displayImage;
      }
    },

    closeImageDialog() {
      this.dialogImage = null;
    },

    selectVariant(variant) {
      this.selectedVariant = variant;
      if (this.dialogImage) this.dialogImage = this.displayImage;
    },

    handleUpscaleClick(variant) {
      if (variant === UPSCALER_VARIANTS.ORIGINAL) {
        this.selectVariant(variant);
        return;
      }

      if (this.upscaleLoading[variant]) return;

      // wenn URL bereits existiert -> nur auswählen
      if (this.upscaleUrls[variant]) {
        this.selectVariant(variant);
        return;
      }

      // sonst Upscale starten
      this.startUpscale(variant);
    },

    async getCustomizationData() {
      this.loadingCustomization = true;
      try {
        const functions = getFunctions();
        const getCustomizationData = httpsCallable(functions, "getCustomizationData");
        await getCustomizationData({id: this.task.customizationData.id});

        // Wichtig:
        // Diese Function muss serverseitig/Firestore das Task-Dokument aktualisieren.
        // Sobald Firestore/Vuex das Task-Update reinbekommt, updated computed upscaleUrls automatisch.
      } catch (error) {
        console.error("Get customization failed:", error);
        EventBus.$emit(events.SNACKBAR, {
          message: "Fehler beim Customization data!",
          type: "error",
        });
      } finally {
        this.loadingCustomization = false;
      }
    },

    async startUpscale(variant) {
      this.$set(this.upscaleLoading, variant, true);

      try {
        const imageUrl = this.taskImage;
        if (!imageUrl) throw new Error("No task image available for upscaling");

        const functions = getFunctions();
        const upscaleGemini = httpsCallable(functions, "upscaleGemini");

        const result = await upscaleGemini({
          artworkUrl: imageUrl,
          originalUrl: this.originalImage,
          promptId: variant,
          resolution: "4K",
        });

        if (!result?.data?.ok) throw new Error("Failed to upscale image");

        // WICHTIG:
        // NICHT local cachen. Wir gehen davon aus, dass die URL im Task-Dokument landet.
        // Optional: direkt auf den Variant switchen, damit der Dialog sofort "auf Default" steht.
        this.selectVariant(variant);

        console.log("Upscaled Image URL:", result.data.url);
      } catch (error) {
        console.error("Upscale failed:", error);
        EventBus.$emit(events.SNACKBAR, {
          message: "Fehler beim Upscalen!",
          type: "error",
        });
      } finally {
        this.$set(this.upscaleLoading, variant, false);
      }
    },

    searchOrderInMails() {
      const terms = [
        this.task.orderName?.split("#")[1],
        this.task.customerEmail,          // Bestell-Email des Kunden
      ].filter(Boolean);

      window.open(
          `https://quelp.ai/inbox?q=${encodeURIComponent(terms.join(","))}`,
          "_blank"
      );

    },

    copyOrderTrackingUrl() {
      navigator.clipboard.writeText(getOrderStatusUrl(this.task));;
    },

    async downloadImage(url, filename, fileType = "png") {
      if (!url) return;
      const furl = await this.resolveStorageUrl(url);
      const filename_with_edit_style = `${filename}_E${this.selectedEditStyle.value}`;
      await downloadImageFromUrl(furl, filename_with_edit_style, () => {
      }, fileType);
    },

    async resolveStorageUrl(input) {
      const storage = getStorage();

      let path = input;

      if (input && input.startsWith("http")) {
        try {
          const url = new URL(input);
          const parts = url.pathname.split("/");
          parts.shift();
          parts.shift();
          path = parts.join("/");
        } catch (e) {
          console.warn("Ungültige URL:", input);
        }
      }

      const fileRef = ref(storage, path);
      return await getDownloadURL(fileRef);
    },
  },
};
</script>

<style lang="scss" scoped>
.card-actions {
  display: flex;
  justify-content: space-between;
}

.selected-variant {
  color: white;
  padding: 4px 8px;
  background: #8a8a8a;
  border-radius: 5px;
}

.card {
  position: relative;
  overflow: hidden;

  .v-card__text {
    padding: 0;
  }
}

/* KI-Bearbeitung: farbig umlaufender Rand solange sie läuft (wie bei den Ratings), grüner Rand wenn fertig.
   Box-Shadow statt Pseudo-Element, weil die Karte overflow: hidden braucht. */
.card--ai-running {
  animation: card-ai-glow 2.4s linear infinite;
}
.card--ai-done {
  box-shadow: 0 0 0 3px #3f9d68, 0 0 14px rgba(63, 157, 104, 0.55) !important;
}
@keyframes card-ai-glow {
  0% { box-shadow: 0 0 0 3px #4285f4, 0 0 16px rgba(66, 133, 244, 0.6); }
  25% { box-shadow: 0 0 0 3px #9b72cb, 0 0 16px rgba(155, 114, 203, 0.6); }
  50% { box-shadow: 0 0 0 3px #d96570, 0 0 16px rgba(217, 101, 112, 0.6); }
  75% { box-shadow: 0 0 0 3px #f2a60c, 0 0 16px rgba(242, 166, 12, 0.6); }
  100% { box-shadow: 0 0 0 3px #4285f4, 0 0 16px rgba(66, 133, 244, 0.6); }
}
.card-ai-badge {
  position: absolute;
  /* unter der oberen Chip-Zeile (Download, Menge, Bestellnummer) */
  top: 60px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 6;
  display: inline-flex;
  align-items: center;
  height: 24px;
  padding: 0 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);

  &--running {
    background: #fff;
    color: #6a4fc4;
  }
  &--done {
    background: #3f9d68;
    color: #fff;

    .v-icon {
      color: #fff !important;
    }
  }
}

.original-image {
  position: absolute;
  bottom: 0;
  right: 0;
  max-height: 30%;
  z-index: 2;
  cursor: pointer;
  border-top-left-radius: 10px;

  /* Zweites Original (Kombi): über dem ersten gestapelt */
  &.original-image-2 {
    bottom: calc(30% + 6px);
    border-bottom-left-radius: 10px;
  }

  &:hover {
    transform: scale(1.1);
    transform-origin: bottom right;
  }
}

.card-overlay--edit {
  z-index: 6;
  background: rgba(224, 148, 27, 0.9) !important;
  padding: 16px;
  &.card-overlay--edit-expired { background: rgba(216, 73, 63, 0.9) !important; }
}
.card-overlay {
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
  top: 0;
  left: 0;
  z-index: 3;
  background: rgba(115, 115, 115, 0.7);
}

.card-header-function {
  z-index: 3;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;

  .v-btn {
    margin: 12px;
  }
}

.image-container {
  position: relative;
}

.image {
  cursor: pointer;
  z-index: 1;
}

.task-product-name {
  padding: 6px;
  border-radius: 5px;
  position: absolute;
  background: rgba(0, 0, 0, 0.87);
  max-width: 50%;
  color: white;
  bottom: 10px;
  margin: 10px;
  z-index: 2;
}

.switch-upscale-container {
  z-index: 4;
  position: relative;
  bottom: 5rem;
  width: 100%;
  display: flex;
  justify-content: center;
}

.task-product-extras{
  padding: 6px;
  border-radius: 5px;
  position: absolute;
  background: rgba(0, 0, 0, 0.87);
  max-width: 50%;
  color: white;
  bottom: 70px;
  margin: 10px;
  z-index: 2;
}

.task-referrer {
  padding: 6px;
  border-radius: 5px;
  position: absolute;
  color: white;
  font-weight: bold;
  max-width: 50%;
  bottom: 78px;
  margin: 10px;
  z-index: 2;
}
</style>
